import { HttpClient } from '../http-client';
import { Timestamp } from '../models';
import { Widget } from './widget';

const RESOURCE_URL = '/dashboards';

// New layout interfaces to match backend model
export interface Cell {
  id: number;
  width: number;
}

export interface Row {
  cells: Cell[];
  cellOrder: number[];
  rowId?: string;
  height: number;
  tempRowId?: number;
}

export interface Layout {
  rows: Row[];
  rowOrder: string[];
  layoutId?: number;
  accountId?: number;
  sourceId?: number;
  sourceType?: string;
}

export interface Dashboard {
  dashboardId: number;
  isDefault: boolean;
  dashboardName: string;
  createdAt: Timestamp;
  updatedAt: Timestamp;
  widgets: Widget[];
  shared: boolean;
  createdBy: number;
  layout?: Layout;
  restrictedIds?: number[];
}

export type DashboardPayload = Pick<Dashboard, 'dashboardName' | 'shared'> & {
  restrictedIds?: number[];
};
export type DuplicateDashboardPayload = Pick<
  Dashboard,
  'dashboardName' | 'shared' | 'dashboardId'
> & {
  restrictedIds?: number[];
};

const getUrl = (id: number) => `${RESOURCE_URL}/${id}`;

class DashboardHttpClient extends HttpClient {
  public init = (): Promise<{ dashboard: Dashboard[] }> => {
    return this.post(`${RESOURCE_URL}/init`);
  };

  public createDashboard = (payload: DashboardPayload): Promise<Dashboard> => {
    return this.post(RESOURCE_URL, payload);
  };

  public updateDashboard = (
    dashboardId: number,
    payload: DashboardPayload,
  ): Promise<Dashboard> => {
    return this.put(getUrl(dashboardId), payload);
  };

  // Add new method to update dashboard layout
  public updateDashboardLayout = (
    dashboardId: number,
    layout: Layout,
  ): Promise<Dashboard> => {
    return this.put(`${getUrl(dashboardId)}/layout`, layout);
  };

  public duplicateDashboard = (
    payload: DashboardPayload,
  ): Promise<Dashboard> => {
    return this.post(`${RESOURCE_URL}/clone`, payload);
  };

  public deleteDashboard = (dashboardId: number): Promise<Dashboard> => {
    return this.delete(getUrl(dashboardId));
  };

  public getAllDashboards = async (): Promise<Dashboard[]> => {
    const result = await this.get<{ dashboard: Dashboard[] }>(RESOURCE_URL);

    // TODO:: Check with backend to remove dashboard key and send the response directly as array.
    return result.dashboard;
  };

  public getDashboard = (dashboardId: number): Promise<Dashboard> => {
    return this.get(`${RESOURCE_URL}/${dashboardId}`);
  };

  public getDashboardV2 = (dashboardId: number): Promise<Dashboard> => {
    return this.get(`${RESOURCE_URL}/dashboard-v2/${dashboardId}`);
  };
}

export const DashboardAPI = new DashboardHttpClient();
